import {
    ADDITIONAL_SERVICE_SUPPORTED_UNITS_SCHEMA,
    AUTOMATIC_SUPPORTED_UNITS_SCHEMA,
    PRICING_STRATEGY,
} from '@schuettflix/interfaces';
import { ProductConfiguration } from '@/shared/models/ContextDataModel/interfaces/entities/product-configuration.ts';
import { PartialAutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { AutomaticProductsFormAdditionalService } from '../schema/getAdditionalServiceSchema.ts';
import {
    parseSellerProduct,
    parseTransportConfiguration,
} from '@/shared/forms/AutomaticProductsForm/domain/parseProductConfiguration.ts';
import { LocationConfiguration } from '@/shared/models/ContextDataModel/interfaces/entities/location-configuration.ts';
import { isAdditionalServiceProduct } from '@/shared/domain/product/isAdditionalServiceProduct.ts';

export function prefillAutomaticProductsForm(
    pricingStrategy: PRICING_STRATEGY,
    productConfigurations: ProductConfiguration[]
): PartialAutomaticProductsFormValues {
    const material = productConfigurations.find(product => product.category === 'MATERIAL');
    const transport = productConfigurations.find(product => product.category === 'TRANSPORT');

    const loadingLocation = transport
        ? parseTransportConfiguration(transport).transportProductConfiguration.additionalProperties.loadingLocation
        : undefined;

    const shared = {
        ...(material && prefillMaterialFormValues(material, loadingLocation)),
        ...(transport && prefillTransportFormValues(transport)),
        additionalServices: prefillAdditionalServices(productConfigurations),
    };

    switch (pricingStrategy) {
        case 'FRANCO':
            return {
                isSalesTransportIncluded: true,
                isPurchaseTransportIncluded: false,
                ...shared,
            };
        case 'MERCHANT':
            return {
                isSalesTransportIncluded: true,
                isPurchaseTransportIncluded: true,
                ...shared,
            };
        case 'STANDARD':
            return {
                isSalesTransportIncluded: false,
                isPurchaseTransportIncluded: false,
                ...shared,
            };
    }
}

function prefillMaterialFormValues(
    materialProduct: ProductConfiguration,
    loadingLocation: LocationConfiguration | undefined
): PartialAutomaticProductsFormValues {
    const additionalProps = materialProduct.additionalProperties;

    const salesMaterialUnit = AUTOMATIC_SUPPORTED_UNITS_SCHEMA.optional().parse(materialProduct.salesUnit);
    const purchaseMaterialUnit = AUTOMATIC_SUPPORTED_UNITS_SCHEMA.optional().parse(materialProduct.purchaseUnit);

    return {
        materialIdentifier: materialProduct.id,
        productType: materialProduct.productType,
        productName: materialProduct.productName,
        currencyCode: materialProduct.currencyCode,
        supplierOrganizationId: materialProduct.partnerOrganizationId ?? undefined,
        salesMaterialQuantity: materialProduct.salesQuantity,
        salesMaterialUnit: salesMaterialUnit,
        purchaseMaterialQuantity: materialProduct.purchaseQuantity,
        purchaseMaterialUnit: purchaseMaterialUnit,
        salesMaterialPrice: materialProduct.salesPrice,
        purchaseMaterialPrice: materialProduct.purchasePrice,
        salesMaterialTaxClassId: materialProduct.salesTaxClassId,
        purchaseMaterialTaxClassId: materialProduct.purchaseTaxClassId,
        showZeroSalesMaterialPrice: materialProduct.showZeroSalesPrice,
        showZeroPurchaseMaterialPrice: materialProduct.showZeroPurchasePrice,
        ...('disposalPurpose' in additionalProps && { disposalPurpose: additionalProps.disposalPurpose }),
        ...('disposalCertificateNumber' in additionalProps && {
            disposalCertificateNumber: additionalProps.disposalCertificateNumber,
        }),
        sellerProduct: parseSellerProduct(materialProduct, loadingLocation),
    };
}

function prefillTransportFormValues(transportProduct: ProductConfiguration): PartialAutomaticProductsFormValues {
    const { transportProductConfiguration, carrierOrganizationId } = parseTransportConfiguration(transportProduct);

    return {
        transportIdentifier: transportProduct.id,
        carrierOrganizationId,
        productName: transportProductConfiguration.productName,
        freightType: transportProductConfiguration.additionalProperties.freightType,
        vehicleClass: transportProductConfiguration.additionalProperties.vehicleClass,
        loadingLocation: transportProductConfiguration.additionalProperties.loadingLocation,
        unloadingLocation: transportProductConfiguration.additionalProperties.unloadingLocation,
        weighingLocation: transportProductConfiguration.additionalProperties.weighingLocation,
        salesTransportQuantity: transportProductConfiguration.salesQuantity,
        salesTransportUnit: transportProductConfiguration.salesUnit,
        purchaseTransportQuantity: transportProductConfiguration.purchaseQuantity,
        purchaseTransportUnit: transportProductConfiguration.purchaseUnit,
        salesTransportPrice: transportProduct.salesPrice,
        purchaseTransportPrice: transportProduct.purchasePrice,
        salesTransportTaxClassId: transportProduct.salesTaxClassId,
        purchaseTransportTaxClassId: transportProduct.purchaseTaxClassId,
        showZeroSalesTransportPrice: transportProduct.showZeroSalesPrice,
        showZeroPurchaseTransportPrice: transportProduct.showZeroPurchasePrice,
        disposalPurpose: transportProductConfiguration.additionalProperties.disposalPurpose,
        disposalCertificateNumber: transportProductConfiguration.additionalProperties.disposalCertificateNumber,
    };
}

function prefillAdditionalServices(
    productConfigurations: ProductConfiguration[]
): AutomaticProductsFormAdditionalService[] {
    return productConfigurations.filter(isAdditionalServiceProduct).map(service => ({
        identifier: service.id,
        productType: service.productType,
        productName: service.productName,
        currencyCode: service.currencyCode,
        partnerOrganizationId: service.partnerOrganizationId,
        salesQuantity: service.salesQuantity,
        salesUnit: ADDITIONAL_SERVICE_SUPPORTED_UNITS_SCHEMA.parse(service.salesUnit),
        purchaseQuantity: service.purchaseQuantity,
        purchaseUnit: ADDITIONAL_SERVICE_SUPPORTED_UNITS_SCHEMA.parse(service.purchaseUnit),
        salesPrice: service.salesPrice,
        purchasePrice: service.purchasePrice,
        salesTaxClassId: service.salesTaxClassId,
        purchaseTaxClassId: service.purchaseTaxClassId,
        showZeroSalesPrice: service.showZeroSalesPrice,
        showZeroPurchasePrice: service.showZeroPurchasePrice,
    }));
}
